import React from 'react';

interface SkeletonProps {
  className?: string;
  variant?: 'text' | 'title' | 'button' | 'avatar' | 'card' | 'field';
  lines?: number;
  width?: string;
  height?: string;
}

export const Skeleton: React.FC<SkeletonProps> = ({
  className = '',
  variant = 'text',
  lines = 1,
  width,
  height
}) => {
  const baseClasses = 'animate-pulse bg-gray-200 dark:bg-gray-700 rounded';

  const variantClasses = {
    text: 'h-4',
    title: 'h-6',
    button: 'h-8 w-20',
    avatar: 'h-10 w-10 rounded-full',
    card: 'h-32',
    field: 'h-10'
  };

  const variantClass = variantClasses[variant];
  const style = {
    ...(width && { width }),
    ...(height && { height })
  };

  if (lines === 1) {
    return (
      <div
        className={`${baseClasses} ${variantClass} ${className}`}
        style={style}
      />
    );
  }

  return (
    <div className={`space-y-2 ${className}`}>
      {Array.from({ length: lines }).map((_, index) => (
        <div
          key={index}
          className={`${baseClasses} ${variantClass}`}
          style={{
            ...style,
            width: index === lines - 1 ? '75%' : '100%'
          }}
        />
      ))}
    </div>
  );
};

// Componenti skeleton specifici per sezioni
export const HeaderSkeleton: React.FC = () => (
  <div className="mb-3">
    <div className="flex items-center justify-between">
      <div className="flex items-center space-x-3">
        <Skeleton variant="button" width="40px" height="40px" />
        <Skeleton variant="title" width="200px" />
      </div>
      <Skeleton variant="button" width="40px" height="40px" />
    </div>
  </div>
);

export const CardSkeleton: React.FC<{ title?: boolean; lines?: number }> = ({
  title = true,
  lines = 3
}) => (
  <div className="glass-card rounded-lg border border-white/20 p-4 space-y-3">
    {title && <Skeleton variant="title" width="150px" />}
    <div className="space-y-2">
      {Array.from({ length: lines }).map((_, index) => (
        <div key={index} className="flex items-center space-x-2">
          <Skeleton variant="text" width="120px" />
          <Skeleton variant="text" width="100px" />
        </div>
      ))}
    </div>
  </div>
);

export const StatusManagerSkeleton: React.FC = () => (
  <div className="glass-card rounded-lg border border-white/20 p-3 space-y-3">
    <div className="flex items-center gap-1.5">
      <Skeleton variant="text" width="16px" height="16px" />
      <Skeleton variant="text" width="80px" />
    </div>

    {/* Stage dropdown skeleton */}
    <div className="space-y-1">
      <Skeleton variant="text" width="60px" height="12px" />
      <Skeleton variant="field" />
    </div>

    {/* Assignee dropdown skeleton */}
    <div className="space-y-1">
      <Skeleton variant="text" width="100px" height="12px" />
      <Skeleton variant="field" />
    </div>

    {/* Source dropdown skeleton */}
    <div className="space-y-1">
      <Skeleton variant="text" width="50px" height="12px" />
      <Skeleton variant="field" />
    </div>
  </div>
);

export const ContactsSkeleton: React.FC = () => (
  <div className="space-y-3">
    {Array.from({ length: 2 }).map((_, index) => (
      <div key={index} className="glass-card rounded-lg border border-white/20 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Skeleton variant="text" width="120px" />
          <Skeleton variant="text" width="60px" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <Skeleton variant="text" width="100px" />
          <Skeleton variant="text" width="140px" />
        </div>
      </div>
    ))}
  </div>
);

export const InterestsSkeleton: React.FC = () => (
  <div className="space-y-2">
    {Array.from({ length: 3 }).map((_, index) => (
      <div key={index} className="flex items-center justify-between p-2 bg-white/5 rounded">
        <Skeleton variant="text" width="150px" />
        <Skeleton variant="text" width="80px" />
      </div>
    ))}
  </div>
);

export const TagsSkeleton: React.FC = () => (
  <div className="flex flex-wrap gap-1">
    {Array.from({ length: 4 }).map((_, index) => (
      <Skeleton key={index} variant="button" width="60px" height="24px" className="rounded-full" />
    ))}
  </div>
);

export const NotesSkeleton: React.FC = () => (
  <div className="space-y-3">
    {Array.from({ length: 3 }).map((_, index) => (
      <div key={index} className="glass-card rounded-lg border border-white/20 p-3 space-y-2">
        <div className="flex items-center justify-between">
          <Skeleton variant="text" width="100px" />
          <Skeleton variant="text" width="80px" />
        </div>
        <Skeleton variant="text" lines={2} />
      </div>
    ))}
  </div>
);

export const CustomFieldsSkeleton: React.FC = () => (
  <div className="space-y-3">
    {Array.from({ length: 4 }).map((_, index) => (
      <div key={index} className="space-y-1">
        <Skeleton variant="text" width="100px" height="12px" />
        <Skeleton variant="field" />
      </div>
    ))}
  </div>
);

// Skeleton per liste di client/leads
export const ListItemSkeleton: React.FC = () => (
  <div className="glass-card rounded-lg border border-white/20 p-4 space-y-3">
    <div className="flex items-start justify-between">
      <div className="flex-1 space-y-2">
        <Skeleton variant="title" width="200px" />
        <div className="flex items-center space-x-2">
          <Skeleton variant="avatar" />
          <Skeleton variant="text" width="120px" />
        </div>
      </div>
      <div className="flex items-center space-x-2">
        <Skeleton variant="button" width="80px" />
        <Skeleton variant="button" width="60px" />
      </div>
    </div>
    <div className="flex items-center space-x-4">
      <Skeleton variant="text" width="100px" />
      <Skeleton variant="text" width="80px" />
      <Skeleton variant="text" width="120px" />
    </div>
  </div>
);

export const ListPageSkeleton: React.FC<{ showFilters?: boolean; itemCount?: number }> = ({
  showFilters = true,
  itemCount = 6
}) => (
  <div className="space-y-6">
    {/* Header skeleton */}
    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
      <Skeleton variant="title" width="200px" />
      <div className="flex items-center space-x-2">
        <Skeleton variant="button" width="100px" />
        <Skeleton variant="button" width="80px" />
        <Skeleton variant="button" width="60px" />
      </div>
    </div>

    {/* Filters skeleton */}
    {showFilters && (
      <div className="flex flex-wrap items-center gap-2">
        <Skeleton variant="button" width="120px" />
        <Skeleton variant="button" width="100px" />
        <Skeleton variant="button" width="80px" />
      </div>
    )}

    {/* Stats skeleton */}
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {Array.from({ length: 4 }).map((_, index) => (
        <div key={index} className="glass-card rounded-lg border border-white/20 p-4">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton variant="text" width="80px" />
              <Skeleton variant="title" width="60px" />
            </div>
            <Skeleton variant="avatar" />
          </div>
        </div>
      ))}
    </div>

    {/* List items skeleton */}
    <div className="space-y-4">
      {Array.from({ length: itemCount }).map((_, index) => (
        <ListItemSkeleton key={index} />
      ))}
    </div>
  </div>
);

// Skeleton per vista Kanban
export const KanbanColumnSkeleton: React.FC = () => (
  <div className="bg-gray-100 dark:bg-gray-800 rounded-lg p-4 min-w-[300px] border border-gray-200 dark:border-gray-700">
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <Skeleton variant="text" width="120px" />
        <Skeleton variant="text" width="30px" />
      </div>
      <Skeleton variant="text" width="100%" height="2px" />
    </div>

    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, index) => (
        <div key={index} className="bg-white dark:bg-gray-700 rounded-lg p-3 border border-gray-200 dark:border-gray-600 space-y-2">
          <Skeleton variant="text" width="90%" />
          <div className="flex items-center justify-between">
            <Skeleton variant="text" width="60px" />
            <Skeleton variant="avatar" width="24px" height="24px" />
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const KanbanSkeleton: React.FC = () => (
  <div className="flex gap-3 sm:gap-4 lg:gap-6 pb-4 h-[calc(100vh-200px)] overflow-x-auto">
    {Array.from({ length: 6 }).map((_, index) => (
      <div key={index} className="flex-shrink-0 w-72 sm:w-80 lg:w-80">
        <KanbanColumnSkeleton />
      </div>
    ))}
  </div>
);

// Skeleton per tabella settings
export const TableSkeleton: React.FC<{ rows?: number }> = ({ rows = 5 }) => (
  <div className="space-y-4">
    {/* Header */}
    <div className="flex gap-4 pb-3 border-b border-black/10">
      <Skeleton width="30%" height="20px" />
      <Skeleton width="25%" height="20px" />
      <Skeleton width="20%" height="20px" />
      <Skeleton width="25%" height="20px" />
    </div>

    {/* Rows */}
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} className="flex gap-4 items-center py-3">
        <Skeleton variant="avatar" />
        <Skeleton width="30%" height="16px" />
        <Skeleton width="25%" height="16px" />
        <Skeleton width="20%" height="16px" />
        <Skeleton width="15%" height="24px" />
      </div>
    ))}
  </div>
);

// Skeleton per form settings
export const FormSkeleton: React.FC<{ fields?: number }> = ({ fields = 5 }) => (
  <div className="space-y-6">
    {Array.from({ length: fields }).map((_, i) => (
      <div key={i} className="space-y-2">
        <Skeleton width="30%" height="14px" />
        <Skeleton width="100%" height="40px" />
        <Skeleton width="80%" height="12px" />
      </div>
    ))}
    <div className="flex gap-3 justify-end pt-4">
      <Skeleton width="100px" height="40px" />
      <Skeleton width="120px" height="40px" />
    </div>
  </div>
);

// Skeleton per tabs commissioni
export const TabsSkeleton: React.FC = () => (
  <div className="space-y-6">
    {/* Tabs */}
    <div className="flex gap-2 bg-black/5 rounded-lg p-1">
      <Skeleton width="120px" height="36px" />
      <Skeleton width="120px" height="36px" />
      <Skeleton width="120px" height="36px" />
      <Skeleton width="120px" height="36px" />
    </div>

    {/* Content */}
    <div className="space-y-4">
      <Skeleton width="100%" height="150px" />
      <div className="grid grid-cols-2 gap-4">
        <Skeleton width="100%" height="80px" />
        <Skeleton width="100%" height="80px" />
      </div>
    </div>
  </div>
);

// Skeleton per lista con gruppi
export const GroupListSkeleton: React.FC<{ items?: number }> = ({ items = 5 }) => (
  <div className="space-y-3">
    {Array.from({ length: items }).map((_, i) => (
      <div key={i} className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
        <div className="flex items-center gap-3 flex-1">
          <Skeleton variant="button" width="16px" height="16px" className="rounded-full" />
          <div className="flex-1 space-y-2">
            <Skeleton width="30%" height="18px" />
            <Skeleton width="50%" height="14px" />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Skeleton width="60px" height="24px" className="rounded-full" />
          <Skeleton width="80px" height="32px" />
        </div>
      </div>
    ))}
  </div>
);